import { useEffect, useState, useCallback } from 'react';
import * as audioCache from '../utils/offline/audioCache';

/**
 * Marks a chapter whose audio is on this device.
 *
 * An indicator, not a control: downloads are started and removed for a whole
 * project from its settings page, so a per-chapter button would be a second
 * way to do the same thing in a row that already carries a title, a played
 * state, a duration and a progress bar.
 *
 * Renders nothing at all unless the chapter is downloaded — including while it
 * is downloading, which is reported per project where the download was
 * started.
 */
const DownloadedIndicator = ({ chapter, className = '' }) => {
  const chapterId = chapter.chapter_id || chapter.id;
  const [downloaded, setDownloaded] = useState(false);

  const refresh = useCallback(async () => {
    const rows = (await audioCache.listDownloads()) || [];
    const mine = rows.find((r) => r.chapterId === chapterId);
    setDownloaded(!!mine && mine.state === audioCache.READY);
  }, [chapterId]);

  useEffect(() => {
    refresh();
    return audioCache.subscribe(refresh);
  }, [refresh]);

  if (!downloaded) return null;

  return (
    <span
      role="img"
      aria-label="Available offline"
      title="Available offline"
      data-testid={`downloaded-${chapterId}`}
      className={`inline-flex text-green-600 dark:text-green-500 ${className}`}
    >
      {/* A download glyph, not a check: the row already uses a green check for
          the played state, and two identical marks a few centimetres apart
          cannot be told apart. */}
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
        />
      </svg>
    </span>
  );
};

export default DownloadedIndicator;
